import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Upload, FileText, Image, Trash2, ShieldCheck, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPE_NAMES = {
  lv: {
    food_safety: 'Pārtikas drošības sertifikāts', bio_cert: 'Bioloģiskais sertifikāts',
    business_license: 'Darbības licence', pvn_cert: 'PVN reģistrācijas apliecība',
    hygiene: 'Higiēnas prasību atbilstība', other: 'Cits dokuments'
  },
  en: {
    food_safety: 'Food Safety Certificate', bio_cert: 'Organic Certificate',
    business_license: 'Business License', pvn_cert: 'VAT Registration',
    hygiene: 'Hygiene Compliance', other: 'Other Document'
  }
};

const DOC_TYPE_ICONS = {
  food_safety: '🛡', bio_cert: '🌿', business_license: '📋',
  pvn_cert: '📄', hygiene: '✅', other: '📎'
};

// Upload + manage documents (for shop owner)
export function ShopDocumentsManager() {
  const { language } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('food_safety');
  const [customName, setCustomName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    try {
      const { data } = await axios.get(`${API}/shop-documents`);
      setDocuments(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError(language === 'lv' ? 'Tikai PDF un attēlu faili' : 'Only PDF and image files');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(language === 'lv' ? 'Fails pārāk liels (max 10MB)' : 'File too large (max 10MB)');
      return;
    }

    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', docType);
      fd.append('custom_type_name', docType === 'other' ? customName : '');
      fd.append('description', description);
      await axios.post(`${API}/shop-documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDescription('');
      setCustomName('');
      fetchDocs();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Upload error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/shop-documents/${docId}`);
      fetchDocs();
    } catch (e) { console.error(e); }
  };

  const names = DOC_TYPE_NAMES[language] || DOC_TYPE_NAMES.lv;

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="shop-documents-manager">
      <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5 text-[#D97736]" />
        {language === 'lv' ? 'Sertifikāti un licences' : 'Certificates & Licenses'}
      </h3>
      <p className="text-sm text-[#5C5C5C] mb-6">
        {language === 'lv'
          ? 'Augšupielādējiet sertifikātus, licences un citus dokumentus. Tie būs publiski redzami jūsu veikala profilā.'
          : 'Upload certificates, licenses and other documents. They will be publicly visible on your shop profile.'}
      </p>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm mb-4">{error}</div>
      )}

      {/* Upload form */}
      <div className="p-4 bg-[#F3F2EE] rounded-xl mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div className="space-y-1.5">
            <Label className="text-sm">{language === 'lv' ? 'Dokumenta veids' : 'Document Type'}</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="h-10 bg-white border-[#E5E4E0]" data-testid="doc-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(names).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {docType === 'other' && (
            <div className="space-y-1.5">
              <Label className="text-sm">{language === 'lv' ? 'Nosaukums' : 'Name'}</Label>
              <Input
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                className="h-10 bg-white border-[#E5E4E0]"
                placeholder={language === 'lv' ? 'Dokumenta nosaukums' : 'Document name'}
                data-testid="custom-name-input"
              />
            </div>
          )}

          <div className={`space-y-1.5 ${docType !== 'other' ? 'sm:col-span-1' : ''}`}>
            <Label className="text-sm">{language === 'lv' ? 'Apraksts (neobligāts)' : 'Description (optional)'}</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="h-10 bg-white border-[#E5E4E0]"
              placeholder={language === 'lv' ? 'Īss apraksts...' : 'Short description...'}
              data-testid="doc-description-input"
            />
          </div>
        </div>

        <label className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-dashed border-[#D97736] rounded-xl cursor-pointer hover:bg-[#D97736]/5 transition-colors">
          <Upload className="w-5 h-5 text-[#D97736]" />
          <span className="text-sm font-medium text-[#D97736]">
            {uploading
              ? (language === 'lv' ? 'Augšupielādē...' : 'Uploading...')
              : (language === 'lv' ? 'Augšupielādēt dokumentu (PDF, JPG, PNG — max 10MB)' : 'Upload document (PDF, JPG, PNG — max 10MB)')}
          </span>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            onChange={handleUpload}
            className="hidden"
            disabled={uploading}
            data-testid="doc-file-input"
          />
        </label>
      </div>

      {/* Documents list */}
      {loading ? (
        <div className="space-y-3">{[...Array(2)].map((_, i) => <div key={i} className="h-16 bg-[#F3F2EE] rounded-xl animate-pulse" />)}</div>
      ) : documents.length > 0 ? (
        <div className="space-y-3">
          {documents.map(doc => (
            <div key={doc.id} className="flex items-center gap-4 p-4 border border-[#E5E4E0] rounded-xl" data-testid={`doc-${doc.id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#F3F2EE] flex items-center justify-center flex-shrink-0">
                {doc.file_type?.startsWith('image') ? (
                  <Image className="w-5 h-5 text-[#4A6B53]" />
                ) : (
                  <FileText className="w-5 h-5 text-[#D97736]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-sm text-[#121212] truncate">
                    {doc.document_type === 'other' && doc.custom_type_name
                      ? doc.custom_type_name
                      : names[doc.document_type] || doc.document_type}
                  </span>
                  {doc.is_verified && (
                    <Badge className="bg-green-600 text-white border-0 text-[10px] px-1.5 py-0">
                      <ShieldCheck className="w-3 h-3 mr-0.5" />
                      {language === 'lv' ? 'Verificēts' : 'Verified'}
                    </Badge>
                  )}
                </div>
                {doc.description && <p className="text-xs text-[#5C5C5C] truncate">{doc.description}</p>}
                <p className="text-xs text-[#5C5C5C]">{doc.filename}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" className="border-[#E5E4E0]">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50" data-testid={`delete-doc-${doc.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{language === 'lv' ? 'Dzēst dokumentu?' : 'Delete document?'}</AlertDialogTitle>
                      <AlertDialogDescription>{doc.filename}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{language === 'lv' ? 'Atcelt' : 'Cancel'}</AlertDialogCancel>
                      <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={() => handleDelete(doc.id)}>
                        {language === 'lv' ? 'Dzēst' : 'Delete'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-sm text-[#5C5C5C] py-4">
          {language === 'lv' ? 'Vēl nav augšupielādētu dokumentu' : 'No documents uploaded yet'}
        </p>
      )}
    </div>
  );
}

// Public documents display (for shop page visitors)
export function ShopDocumentsPublic({ userId }) {
  const { language } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) fetchDocs();
  }, [userId]);

  const fetchDocs = async () => {
    try {
      const { data } = await axios.get(`${API}/shop-documents/${userId}/public`);
      setDocuments(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const names = DOC_TYPE_NAMES[language] || DOC_TYPE_NAMES.lv;

  if (loading) return null;
  if (documents.length === 0) return null;

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="shop-documents-public">
      <h3 className="font-outfit font-semibold text-[#121212] mb-4 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-[#4A6B53]" />
        {language === 'lv' ? 'Sertifikāti un licences' : 'Certificates & Licenses'}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {documents.map(doc => (
          <a
            key={doc.id}
            href={doc.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 bg-[#F3F2EE] rounded-xl hover:bg-[#E5E4E0] transition-colors group"
            data-testid={`public-doc-${doc.id}`}
          >
            <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
              {doc.file_type?.startsWith('image') ? (
                <Image className="w-4 h-4 text-[#4A6B53]" />
              ) : (
                <FileText className="w-4 h-4 text-[#D97736]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-[#121212] truncate">
                  {doc.document_type === 'other' && doc.custom_type_name
                    ? doc.custom_type_name
                    : names[doc.document_type] || doc.document_type}
                </span>
                {doc.is_verified && (
                  <ShieldCheck className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                )}
              </div>
              {doc.description && <p className="text-xs text-[#5C5C5C] truncate">{doc.description}</p>}
            </div>
            <ExternalLink className="w-4 h-4 text-[#5C5C5C] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </a>
        ))}
      </div>
    </div>
  );
}
